import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, RotateCcw } from 'lucide-react';

// Plays a video hosted on Bunny Stream and, when it finishes, covers it with a
// "See If You Are A Fit" prompt (Bunny shows no suggested videos of its own).
//
// How the page and the player talk: Bunny's player follows the standard
// "player.js" messaging rules. When it says it is ready we ask it to tell us
// about 'ended' and 'play', and it then posts those events back to this page.

const EMBED_URL = 'https://iframe.mediadelivery.net/embed';
const PLAYERJS_VERSION = '0.0.11';

interface BunnyVideoProps {
  libraryId: string;
  videoId: string;
  title: string;
  onCta: () => void;
  // Testimonial strip only: jump to the next story card.
  onNext?: () => void;
  // Smaller text/buttons for the square cards in the testimonial strip.
  compact?: boolean;
  // Start playing as soon as the video appears (the browser may still block this).
  autoplay?: boolean;
  // Set to false to skip the "Your turn." prompt when the video ends (e.g. the VSL).
  showEndScreen?: boolean;
}

// Messages travel as JSON text; accept an already-parsed object as well.
const readMessage = (raw: unknown): any => {
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return raw && typeof raw === 'object' ? raw : null;
};

export default function BunnyVideo({ libraryId, videoId, title, onCta, onNext, compact = false, autoplay = false, showEndScreen = true }: BunnyVideoProps) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [ended, setEnded] = useState(false);

  const send = (method: string, value?: string) =>
    frameRef.current?.contentWindow?.postMessage(
      JSON.stringify({ context: 'player.js', version: PLAYERJS_VERSION, method, value, listener: value }),
      '*'
    );

  const listen = () => {
    send('addEventListener', 'ended');
    send('addEventListener', 'play');
  };

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      // Only trust messages that really come from this video's own player.
      if (e.source !== frameRef.current?.contentWindow) return;
      const data = readMessage(e.data);
      if (!data || data.context !== 'player.js') return;
      if (data.event === 'ready') listen();
      if (data.event === 'ended') setEnded(true);
      if (data.event === 'play') setEnded(false);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [videoId]);

  const watchAgain = () => {
    setEnded(false);
    send('play');
  };

  return (
    <div className="relative w-full h-full bg-black">
      <iframe
        ref={frameRef}
        src={`${EMBED_URL}/${libraryId}/${videoId}?autoplay=${autoplay}&loop=false&muted=false&preload=true`}
        loading={autoplay ? 'eager' : 'lazy'}
        className="absolute inset-0 w-full h-full border-0"
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        title={title}
        onLoad={listen}
      ></iframe>

      {showEndScreen && ended && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-black/85 px-4 text-center animate-in fade-in duration-300">
          <p className={`text-white font-black leading-tight ${compact ? 'text-xl' : 'text-3xl'}`}>Your turn.</p>
          <button
            onClick={onCta}
            className={`inline-flex items-center justify-center gap-2 bg-red-700 hover:bg-red-800 text-white font-black uppercase tracking-wide rounded-md transition-colors ${compact ? 'text-xs px-4 py-3' : 'text-base px-8 py-4'}`}
          >
            See If You Are A Fit <ArrowRight className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
          </button>
          {onNext && (
            <button
              onClick={onNext}
              className="text-white text-xs font-bold uppercase tracking-wide border-2 border-white/70 hover:border-white rounded-md px-4 py-2.5 transition-colors"
            >
              Watch another story →
            </button>
          )}
          <button
            onClick={watchAgain}
            className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-xs font-bold underline underline-offset-4 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Watch again
          </button>
          {!compact && <p className="text-white/70 text-sm font-medium">No pressure, just a conversation</p>}
        </div>
      )}
    </div>
  );
}
